import dayjs from "dayjs"
import { DateNumber, DateRange, DateRangeString, DateString } from "types/global"
import { dateWithoutTime, nowWithoutTime } from "./time"
import { dateToString } from "./timeFormat"



/**
 * Controlla se "start" è minore di "end" eventualmente li inverte
 * e se non ci sono valori provo a usare il parametro "def"
 */
export function normalizeDateRange(range: DateRange, def?: DateRange, allowNull: boolean = false): DateRange {
	if (!range) range = def
	if (!range) return {}
	if (range.start == null) range.start = def?.start ?? null
	if (range.end == null) range.end = def?.end ?? null
	if (!allowNull) {
		if (!range.start && range.end) range.start = range.end
		if (range.start && !range.end) range.end = range.start
	}
	if (range.start && range.end) {
		range = { start: Math.min(range.start, range.end), end: Math.max(range.start, range.end) }
	}
	return range
}

/**
 * Normalizza un array di DateRange in un unico DateRange che include tutti i range dell'array
 * se un range è null o non ha valori, viene ignorato
 * se tutti i range sono null o non hanno valori, restituisce un range vuoto
 */
export function normalizeDateRanges(ranges: DateRange[]): DateRange {
	if (!ranges) ranges = []
	let rangeRet: DateRange = {}
	for (let range of ranges) {
		if (!range) range = {}
		if (rangeRet.start == null) rangeRet.start = range.start
		if (rangeRet.end == null) rangeRet.end = range.end
		if (!!rangeRet.start && !!rangeRet.end) {
			rangeRet = { start: Math.min(rangeRet.start, rangeRet.end), end: Math.max(rangeRet.start, rangeRet.end) }
			break
		}
	}
	if (!rangeRet.start && rangeRet.end) rangeRet.start = rangeRet.end
	if (rangeRet.start && !rangeRet.end) rangeRet.end = rangeRet.start
	return rangeRet
}

/** 
 * Restituisce un RANGE da una data di inizio e un numero di giorni 
 */
export function buildDateRange(start: any, days: number = 0): DateRange {
	const dj = dayjs(start).startOf("day")
	if (!dj.isValid()) return {}
	return {
		start: dj.valueOf(),
		end: dj.add(days, "days").valueOf()
	}
}

/**
 * Restituisce un RANGE da due DATA STRING nel formato "YYYY-MM-DD"
 */
export function stringToDateRange(start: DateString, end: DateString): DateRange {
	return {
		start: dayjs(start).startOf("day").valueOf(),
		end: dayjs(end).startOf("day").valueOf()
	}
}

/**
 * il numero di giorni in un DATE-RANGE (estremi inclusi)
 * se il range è ristretto ad una singola data restituisce 0
 * se il range è vuoto o non ha valori restituisce 0
 */
export function daysInDateRange(range: DateRange): number {
	if (!haveValueDateRange(range)) return 0
	return dayjs(range.end).diff(range.start, "day")
}

/**
 * Esegue una callback per ogni giorno del range (estremi inclusi).
 * Se la callback restituisce `false`, il ciclo si interrompe.
 */
export function forEachDayInDateRange(
	range: DateRange,
	callback: (date: DateNumber, index: number) => boolean | void,
): void {
	if (!haveValueDateRange(range) || !callback) return
	for (
		let current = dayjs(range.start).startOf("day"), i = 0;
		current.isSameOrBefore(range.end);
		current = current.add(1, "day"), i++
	) {
		if (callback(current.valueOf(), i) === false) return
	}
}

/**
 * Trasforma un DateRange numerico in DateRangeString (senza time)
 * Per compatibilità con DateRangeString
 */
export function dateRangeToDateRangeString(range: DateRange): DateRangeString {
	if (!range) return null
	return {
		start: dateToString(range.start),
		end: dateToString(range.end)
	}
}

/** 
 * restituisce una stringa rappresentativa del DATE-RANGE 
 */
export function humanDateRange(dateRange: DateRange): string {
	if (!haveValueDateRange(dateRange)) return null
	if (oneDayDateRange(dateRange)) {
		return dayjs(dateRange.start).format("DD/MM/YYYY")
	}
	return `${dayjs(dateRange.start).format("DD/MM/YY")} - ${dayjs(dateRange.end).format("DD/MM/YY")}`
}

/**
 * restituisce true quando i due DATE-RANGE hanno almeno una sovrapposizione
 */
export function overlapDateRange(dateRange: DateRange, filter: DateRange): boolean {
	const outStart = dateRange.start && filter.end && dateRange.start > filter.end
	const outEnd = dateRange.end && filter.start && dateRange.end < filter.start
	return !outStart && !outEnd
}

/**
 * Restituisce l'intersezione minima tra due DATE-RANGE (estremi inclusi).
 * Se i range non si sovrappongono restituisce `{}`.
 * r1 = [10, 20], r2 = [15, 30]
 * restituisce [15, 20]
 */
export function intersectDateRanges(r1: DateRange, r2: DateRange): DateRange {
	if (!r1 || !r2) return {}
	const start = r1.start == null ? r2.start
		: r2.start == null ? r1.start : Math.max(r1.start, r2.start)
	const end = r1.end == null ? r2.end
		: r2.end == null ? r1.end : Math.min(r1.end, r2.end)
	if (start != null && end != null && start > end) return {}
	return { start, end }
}

/** 
 * dati due DATE-RANGE restituisce un DATE-RANGE che li include entrambi 
 * r1 = [10, 20], r2 = [15, 30]
 * restituisce [10, 30]
 */
export function addDateRange(d1: DateRange, d2: DateRange): DateRange {
	return {
		start: !d1.start ? d2.start : !d2.start ? d1.start : Math.min(d1.start, d2.start),
		end: !d1.end ? d2.end : !d2.end ? d1.end : Math.max(d1.end, d2.end)
	}
}

/** 
 * restituisce "true" se il range "test" è completamente incluso nel range "container"
 * se "container" non ha valori restituisce false
 * se "test" non ha valori restituisce true
 */
export function includeDateRange(container: DateRange, test: DateRange): boolean {
	if (!haveValueDateRange(container)) return false
	if (!haveValueDateRange(test)) return true
	return container.start <= test.start && container.end >= test.end
}

/**
 * Restituisce true se il range è ristretto ad una singola date
 */
export function oneDayDateRange(range: DateRange): boolean {
	return range?.start && (!range.end || range.start == range.end)
}

/**
 * Restituisce true se la data è nel range
 * se il range non ha valori restituisce true
 */
export function betweenDateRange(date: DateNumber, range: DateRange): boolean {
	if (!haveValueDateRange(range)) return true
	const start = range.start ? dateWithoutTime(range.start) : null
	const end = range.end ? dateWithoutTime(range.end) : null
	date = dateWithoutTime(date)
	return (start == null || start <= date) && (end == null || end >= date)
}

/** 
 * restituisce `true` se due DATE-RANGE sono uguali come contenuti 
 */
export function equalDateRanges(range1: DateRange, range2: DateRange): boolean {
	return (range1 == range2) || (range1.start == range2.start && range1.end == range2.end)
}

/**
 * restituisce true se il range ha sia uno "start" che un "end"
 */
export function haveValueDateRange(range: DateRange): boolean {
	return !!range && !!range.start && !!range.end
}

/** 
 * dato un DATE-RANGE 
 * restituisce i giorni da "sfalsare" 
 * per poter centrare il numero di giorni "days"
 * (utilizzato per centrare i giorni selezionati e il cycles selezionato)
 * */
export function centerDateRange(rangeToCenter: DateRange, days: number): number {
	const rangeDays = daysInDateRange(rangeToCenter)
	return Math.round((days - rangeDays) / 2)
}

/** 
 * Restituisce un DATE-RANGE settato al giorno di oggi 
 */
export function todayDateRange(): DateRange {
	const today = nowWithoutTime()
	return { start: today, end: today }
}

/**
 * data una data di tipo numerico setta il DataRange in modo da includere quella data
 */
export function includeDateInDateRange(dateRange: DateRange, date: DateNumber): DateRange {
	if (!dateRange) dateRange = {}
	if (dateRange.start == null || date < dateRange.start) dateRange.start = date
	if (dateRange.end == null || date > dateRange.end) dateRange.end = date
	return dateRange
}

/**
 * dato un oggetto range:DateRange
 * lo espande sia come inizio che come fine di un dato numero di giorni
 */
export function expandDateRange(range: DateRange, startDays: number, endDays?: number): DateRange {
	if (!range) return range
	const newRange: DateRange = { ...range }
	if (newRange.start != null) {
		newRange.start = dayjs(newRange.start).subtract(startDays, "day").valueOf()
	}
	if (newRange.end != null) {
		newRange.end = dayjs(newRange.end).add(endDays ?? startDays, "day").valueOf()
	}
	return newRange
}